import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { GetCaseLineItemDetail, processAction } from 'src/services/WorkflowService'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { ChronoCompleteRequest } from 'src/viewModels/workflow/ChronoCompleteRequest'

interface ICompleteChronoRequestModalProps {
  closeModal: () => void
  isOpen: boolean
  inboxItemId: number
  inboxId: string
  action: InboxActionSummary
  data: ObjectiveProcessAction
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  associatedItemId?: string
}

function CompleteChronoRequestModal(props: ICompleteChronoRequestModalProps) {
  const [lineItemOptions, setLineItemOptions] = useState<Array<DropdownOption>>([])
  const [selectedLineItemId, setSelectedLineItemId] = useState<string>('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {}, [props.isOpen])

  useEffect(() => {
    const fetchData = async () => {
      GetCaseLineItemDetail(+(props.associatedItemId ?? 0), false)
        .then((response) => {
          setLineItemOptions(response)

          if (response.length === 1) {
            setSelectedLineItemId(response[0].id)
          }
        })
        .catch(() => {
          setLineItemOptions(new Array())
        })
    }

    fetchData()
  }, [])

  const handlesOnClose = () => {
    props.closeModal()
  }

  const validationSchema = Yup.object({
    caseLineItemId: Yup.string().required('Line item is required'),
    chronoCompleteDate: Yup.date()
      .max(new Date(), 'Date cannot be in the future')
      .min(new Date('2000-01-01'), 'Date must be on or after January 1, 2000')
      .test('is-valid-date', 'Date must be a valid date', () => {
        const dateInput = document.getElementById(
          'chronoCompleteDate'
        ) as HTMLInputElement | null

        if (dateInput && dateInput.value === '') {
          if (dateInput.validity.badInput) {
            return false
          }
        } else {
          return true
        }

        return true
      })
      .test(
        'Chrono-date-required-with-number',
        'Date of Chrono Complete is required',
        function (value) {
          return value !== null && value !== undefined
        }
      ),
    numberOfHours: Yup.number()
      .required('Number of hours is required')
      .positive('Number of hours must be positive'),
    chronoBy: Yup.string().required('Chrono By is required'),
    comments: Yup.string().when('requireComment', {
      is: false,
      then: (schema: any) => schema.notRequired(),
      otherwise: (schema: any) => schema.required('Comments are required'),
    }),
  })

  const formInitialValues = {
    caseLineItemId: selectedLineItemId,
    chronoCompleteDate: moment().format('YYYY-MM-DD'),
    numberOfHours: '',
    chronoBy: '',
    comments: '',
    requireComment: props.action.requireComment,
  }

  const isInitialValid = validationSchema.isValidSync(formInitialValues)

  async function processChronoComplete(values: any) {
    setLoading(true)

    let data = new ObjectiveProcessAction()

    const chronoRequest: ChronoCompleteRequest = {
      caseLineItemId: +values.caseLineItemId,
      chronoCompleteDate: new Date(
        moment(values.chronoCompleteDate, 'YYYY-MM-DD').toISOString()
      ),
      numberOfHours: parseFloat(values.numberOfHours),
      chronoBy: values.chronoBy,
    }

    data.chronoCompleteRequest = chronoRequest

    if (values.comments) {
      data.comment = values.comments
    }

    processAction(data, props.action.id.toString(), props.inboxItemId.toString())
      .then(() => {
        props.handleProcessActionFinish!(props.action)
        setLoading(false)
        props.closeModal()
      })
      .catch(() => {
        setLoading
        props.closeModal()
      })
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={props.isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handlesOnClose()
        }
      }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={props.action.actionName} isModal={true} />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px' }}>
        {loading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          initialValues={formInitialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          isInitialValid={isInitialValid}
          onSubmit={processChronoComplete}
        >
          {({ values, handleChange, errors, touched, isValid, handleBlur }) => (
            <Form>
              <Grid container direction="column" spacing={2}>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!(touched.caseLineItemId && errors.caseLineItemId)}
                    required
                  >
                    <InputLabel
                      shrink={true}
                      variant="outlined"
                      style={{ color: errors.caseLineItemId ? 'red' : undefined }}
                    >
                      Line Item
                    </InputLabel>
                    <Select
                      onBlur={handleBlur}
                      variant="outlined"
                      input={<OutlinedInput notched label="Line Item" />}
                      inputProps={{ label: true, notched: true }}
                      fullWidth
                      id="caseLineItemId"
                      native={true}
                      name="caseLineItemId"
                      value={values.caseLineItemId}
                      onChange={handleChange}
                      error={!!(touched.caseLineItemId && errors.caseLineItemId)}
                      disabled={lineItemOptions.length === 1}
                    >
                      <option value="">Select a line item</option>
                      {lineItemOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.description}
                        </option>
                      ))}
                    </Select>
                    {touched.caseLineItemId && errors.caseLineItemId && (
                      <FormHelperText>{errors.caseLineItemId as string}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item direction="row" container xs={4}>
                  <TextField
                    onBlur={handleBlur}
                    inputProps={{ id: 'chronoCompleteDate' }}
                    InputLabelProps={{ shrink: true }}
                    type="date"
                    name="chronoCompleteDate"
                    variant="outlined"
                    value={values.chronoCompleteDate}
                    onChange={handleChange}
                    label="Date of Chrono Complete"
                    fullWidth
                    required
                    error={!!(touched.chronoCompleteDate && errors.chronoCompleteDate)}
                    helperText={
                      (touched.chronoCompleteDate && errors.chronoCompleteDate) as string
                    }
                  />
                </Grid>

                <Grid item direction="row" container xs={4}>
                  <TextField
                    onBlur={handleBlur}
                    inputProps={{ id: 'numberOfHours' }}
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    name="numberOfHours"
                    variant="outlined"
                    value={values.numberOfHours}
                    onChange={handleChange}
                    label="Number of Hours for Chrono"
                    fullWidth
                    required
                    error={!!(touched.numberOfHours && errors.numberOfHours)}
                    helperText={(touched.numberOfHours && errors.numberOfHours) as string}
                  />
                </Grid>

                <Grid item direction="row" container xs={4}>
                  <TextField
                    onBlur={handleBlur}
                    inputProps={{ id: 'chronoBy' }}
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    name="chronoBy"
                    variant="outlined"
                    value={values.chronoBy}
                    onChange={handleChange}
                    label="Chrono By"
                    fullWidth
                    required
                    error={!!(touched.chronoBy && errors.chronoBy)}
                    helperText={(touched.chronoBy && errors.chronoBy) as string}
                  />
                </Grid>

                {props.action.displayComment && (
                  <Grid item xs={12}>
                    <TextField
                      onBlur={handleBlur}
                      inputProps={{ id: 'comments' }}
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      name="comments"
                      variant="outlined"
                      value={values.comments}
                      onChange={handleChange}
                      label="Comments"
                      fullWidth
                      multiline
                      minRows={4}
                      required={props.action.requireComment}
                      error={!!(touched.comments && errors.comments)}
                      helperText={(touched.comments && errors.comments) as string}
                    />
                  </Grid>
                )}
              </Grid>

              <DialogActions>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={loading || !isValid}
                >
                  Execute
                </Button>
                <Button onClick={handlesOnClose} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default CompleteChronoRequestModal
